import { X } from 'lucide-react';
import { Badge, Button, Card } from '@pixlide/ui';
import type { TemplateAnimationInspection } from '@/api/queries/templates';
import { fieldKindLabels } from '@/features/templates/template-ui';

/**
 * Résultat de l'analyse du Lottie juste après son dépôt : champs créés automatiquement
 * depuis les calques `slot-…`, et points d'attention (calques ignorés, ratio incompatible,
 * images non embarquées, polices absentes du serveur).
 */
export function InspectionSummary({
  inspection,
  onClose,
}: {
  inspection: TemplateAnimationInspection;
  onClose: () => void;
}) {
  const count = inspection.detectedFields.length;

  return (
    <Card className="flex flex-col gap-3 p-6">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold">
            {count} champ{count > 1 ? 's' : ''} détecté{count > 1 ? 's' : ''}
          </h3>
          <p className="text-sm text-ink-500">
            Animation {inspection.width}×{inspection.height} · {inspection.durationSeconds} s
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Fermer le récapitulatif">
          <X aria-hidden className="size-4" />
        </Button>
      </div>

      {count > 0 ? (
        <ul className="flex flex-col gap-1 text-sm">
          {inspection.detectedFields.map((field) => (
            <li key={field.key} className="flex flex-wrap items-center gap-2">
              <Badge tone="neutral">{fieldKindLabels[field.kind]}</Badge>
              <span className="font-medium">{field.label}</span>
              <code className="text-xs text-ink-300">{field.key}</code>
              {field.kind === 5 && field.defaultValue ? (
                <span
                  aria-hidden
                  className="size-3.5 rounded-full ring-1 ring-ink-100"
                  style={{ backgroundColor: field.defaultValue }}
                />
              ) : null}
              {field.kind !== 5 && field.defaultValue ? (
                <span className="truncate text-ink-500">« {field.defaultValue} »</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {inspection.warnings.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {inspection.warnings.map((warning) => (
            <li key={warning} className="text-xs text-warning-500">
              {warning}
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}
